import { Minus, Plus, ShoppingCart, Trash2, TriangleAlert, X } from "lucide-react";
import { useMemo } from "react";

import { computeTotals, useCartStore } from "@/stores/cart.ts";
import { formatPEN } from "@/lib/utils.ts";
import { useStock } from "@/queries/inventory.ts";
import { useTerminalStore } from "@/stores/terminal.ts";

interface Props {
  onCheckout: () => void;
}

export function Cart({ onCheckout }: Props) {
  const lines = useCartStore((s) => s.lines);
  const setQuantity = useCartStore((s) => s.setQuantity);
  const removeProduct = useCartStore((s) => s.removeProduct);
  const clear = useCartStore((s) => s.clear);

  const totals = computeTotals(lines);

  // Stock de la sucursal de la caja: solo para avisar, la venta no se bloquea.
  const branchId = useTerminalStore((s) => s.terminal?.branchId);
  const stock = useStock({ branchId });
  const stockById = useMemo(
    () => new Map((stock.data ?? []).map((s) => [s.product_id, Number(s.stock)])),
    [stock.data],
  );
  const empty = lines.length === 0;

  return (
    <div className="flex h-full flex-col rounded-xl border border-slate-200 bg-white">
      <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
        <div className="flex items-center gap-2">
          <ShoppingCart className="h-5 w-5 text-slate-700" />
          <h3 className="text-sm font-semibold text-slate-800">
            Carrito
            {lines.length > 0
              ? ` · ${totals.itemCount} ítem${totals.itemCount > 1 ? "s" : ""}`
              : ""}
          </h3>
        </div>
        {!empty && (
          <button
            type="button"
            onClick={clear}
            className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-slate-500 hover:bg-slate-100"
          >
            <Trash2 className="h-3.5 w-3.5" /> Limpiar
          </button>
        )}
      </div>

      <div className="flex-1 overflow-auto">
        {empty ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 p-10 text-slate-400">
            <ShoppingCart className="h-10 w-10" />
            <p className="text-sm">Sin productos. Agrega desde la búsqueda.</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {lines.map((line) => {
              const lineTotal = Number(line.product.base_price) * line.quantity;
              return (
                <li key={line.productId} className="px-4 py-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-slate-900">
                        {line.product.name}
                      </p>
                      <p className="text-xs text-slate-500">
                        {formatPEN(line.product.base_price)} c/u
                      </p>
                      {line.product.track_inventory &&
                        stockById.has(line.productId) &&
                        line.quantity > (stockById.get(line.productId) ?? 0) && (
                          <p className="mt-0.5 flex items-center gap-1 text-xs font-medium text-amber-700">
                            <TriangleAlert className="h-3.5 w-3.5" />
                            Stock disponible: {Math.max(stockById.get(line.productId) ?? 0, 0)}
                          </p>
                        )}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeProduct(line.productId)}
                      aria-label={`Quitar ${line.product.name}`}
                      className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setQuantity(line.productId, line.quantity - 1)}
                        className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200"
                      >
                        <Minus className="h-4 w-4" />
                      </button>
                      <span className="min-w-[2.5rem] text-center text-sm font-medium tabular-nums">
                        {line.quantity}
                      </span>
                      <button
                        type="button"
                        onClick={() => setQuantity(line.productId, line.quantity + 1)}
                        className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                    <span className="text-sm font-semibold tabular-nums text-slate-900">
                      {formatPEN(lineTotal)}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="border-t border-slate-200 px-4 py-3">
        <div className="space-y-1 text-sm">
          <Row label="Subtotal" value={formatPEN(totals.subtotal)} />
          <Row label="IGV" value={formatPEN(totals.igvAmount)} />
          <div className="flex items-baseline justify-between border-t border-slate-100 pt-2">
            <span className="text-base font-semibold text-slate-900">Total</span>
            <span className="text-2xl font-bold tabular-nums text-slate-900">
              {formatPEN(totals.total)}
            </span>
          </div>
        </div>
        <button
          type="button"
          onClick={onCheckout}
          disabled={empty}
          className="mt-4 w-full rounded-xl bg-emerald-600 py-4 text-base font-semibold text-white shadow-sm hover:bg-emerald-700 active:bg-emerald-800 disabled:cursor-not-allowed disabled:bg-slate-300"
        >
          Cobrar {formatPEN(totals.total)}
        </button>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between text-slate-600">
      <span>{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}
